import { type FC } from 'react';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';

import { ViewInRoom } from '../../respShared/PdpRespSharedHeroCarousel/CarouselItems/ViewInRoom.tsx';
import { HeroCarouselRequestMore } from './HeroCarouselRequestMore.tsx';
import { isPhoto, type CarouselItem } from './types.ts';
import { SRC_SET_WIDTHS, SIZES } from './constants.ts';

import { type PdpRespHeroCarousel_item$data } from './__generated__/PdpRespHeroCarousel_item.graphql.ts';
import { type PdpRespHeroCarousel_user$data } from './__generated__/PdpRespHeroCarousel_user.graphql.ts';

import styles from './PreviewOverlay.scss';

const overlayClassName = classnames(
    dibsCss.absolute,
    dibsCss.inset0,
    dibsCss.flex,
    dibsCss.flexCol,
    dibsCss.itemsCenter,
    dibsCss.justifyCenter,
    dibsCss.zBump,
    dibsCss.bgMoonstone,
    styles.previewOverlay
);

export const PreviewOverlay: FC<{
    previewImage: { src: string; alt: string } | null;
    previewIndex: number | null;
    carouselItems: CarouselItem[];
    user: PdpRespHeroCarousel_user$data | null | undefined;
    item: PdpRespHeroCarousel_item$data;
    currency: string;
    disableModals?: boolean;
}> = ({ previewImage, previewIndex, carouselItems, user, item, currency, disableModals }) => {
    if (previewImage) {
        return (
            <div className={overlayClassName}>
                <img
                    className={classnames(
                        dibsCss.block,
                        dibsCss.wFull,
                        dibsCss.objectScaleDown,
                        styles.previewImage
                    )}
                    src={previewImage.src}
                    alt={previewImage.alt}
                    srcSet={getSrcsetString(previewImage.src, SRC_SET_WIDTHS)}
                    sizes={SIZES}
                />
            </div>
        );
    }

    if (previewIndex === null) {
        return null;
    }

    const previewItem = carouselItems[previewIndex];

    if (previewItem?.__type === 'viewInRoom' && isPhoto(carouselItems[0])) {
        return (
            <div className={classnames(overlayClassName, styles.previewViewInRoom)}>
                <ViewInRoom
                    key={previewIndex}
                    user={user}
                    item={item}
                    viewInRoomPhoto={previewItem}
                    mainImagePath={{
                        imagePath: carouselItems[0].largePath,
                        masterOrZoomPath: carouselItems[0].masterOrZoomPath,
                    }}
                />
            </div>
        );
    }

    if (previewItem?.__type === 'requestMore') {
        return (
            <div className={overlayClassName}>
                <HeroCarouselRequestMore
                    user={user}
                    item={item}
                    currency={currency}
                    disableRequestMoreImagesModal={disableModals}
                />
            </div>
        );
    }

    return null;
};
